import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { INTL_2_DIGIT_MONTH, INTL_SHORT_MONTH } from 'constants/dateFormats'
import { EmptyArray, fillArray } from 'lib/array/arrayUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useMemo } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`

const YearContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const MonthsGrid = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-template-columns: repeat(3, 1fr);

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const MonthButton = styled(Clickable)`
  height: ${rem(40)};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: ${props => props.theme.palette.neutral.default.one};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.S};
  transition: 200ms background-color, 200ms border-color, 200ms color;

  &:disabled {
    color: ${props => props.theme.palette.neutral.default.four};
    border-color: ${props => props.theme.palette.neutral.default.seven}
  }

  &.picked {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.highlight.primary.normalContrast};
    border-color: transparent;
  }
`

/**
 * YYYY-MM
 */
export type PickedMonth = `${number}-${number}`

export function parsePickedMonth(value: PickedMonth): { year: number; month: number; } {
  const date = new Date(value)
  const year = date.getFullYear()
  const month = date.getMonth() + 1

  return { year, month }
}

export type MonthPickerChangeHandler = (value: Set<PickedMonth>) => void

interface Props {
  /** inclusive */
  min: PickedMonth
  /** inclusive */
  max: PickedMonth
  value: Set<PickedMonth>
  onChange: MonthPickerChangeHandler
}

function MonthPicker({
  value,
  min,
  max,
  onChange,
}: Props) {
  const format = useIntlDateFormatter()
  const options = useMemo<Array<[year: number, months: Array<{ value: string; label: string; disabled?: boolean }>]>>(() => {
    const { year: minYear, month: minMonth } = parsePickedMonth(min)
    const { year: maxYear, month: maxMonth } = parsePickedMonth(max)

    if (minYear > maxYear) {
      return EmptyArray
    }
    // Why +1? Because min and max years are inclusive.
    const yearsMonths: Array<[year: number, months: Array<{ value: string; label: string; disabled?: boolean }>]> = fillArray(maxYear - minYear + 1, (index) => {
      const year = index + minYear
      if (year === minYear) {
        if (minMonth > 12) return [year, []]
        return [year, fillArray(12, (i) => ({ disabled: (i + 1) < minMonth, value: format(new Date(year, i), INTL_2_DIGIT_MONTH), label: format(new Date(year, i), INTL_SHORT_MONTH) }))]
      }
      if (year === maxYear) {
        return [year, fillArray(12, (i) => ({ disabled: (i + 1) > maxMonth, value: format(new Date(year, i), INTL_2_DIGIT_MONTH), label: format(new Date(year, i), INTL_SHORT_MONTH) }))]
      }
      return [year, fillArray(12, i => ({ value: format(new Date(year, i), INTL_2_DIGIT_MONTH), label: format(new Date(year, i), INTL_SHORT_MONTH) }))]
    })

    return yearsMonths.filter(([, months]) => months.length)
  }, [format, max, min])

  const handleMonthClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    const option = String(event.currentTarget.dataset.option) as PickedMonth
    const wasPicked = event.currentTarget.dataset.picked === 'true'
    const nextValue = new Set(value.values())
    if (wasPicked) {
      nextValue.delete(option)
    } else {
      nextValue.add(option)
    }
    onChange(nextValue)
  }, [onChange, value])

  return <Container>
    {options.map(([year, months]) => <YearContainer key={year}>
      <Heading variant="heading5">{year}</Heading>
      <MonthsGrid>
        {months.map((month, monthIndex) => {
          const option: PickedMonth = `${year}-${monthIndex + 1}`
          const picked = value.has(option)
          return <MonthButton
            key={option}
            data-option={option}
            data-picked={picked}
            disabled={month.disabled}
            className={clsx({ picked })}
            onClick={handleMonthClick}
          >
            <BodyText variant="medium" weight={picked ? 'bold' : 'normal'}>{month.label}</BodyText>
          </MonthButton>
        })}
      </MonthsGrid>
    </YearContainer>)}
  </Container>
}

export default MonthPicker
